class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('.cart-notification__close, .cart-notification__links > button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    if (this.header) this.header.reveal();
    this.open();
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);

if (!window.TWFCartUpsellReady) {
  window.TWFCartUpsellReady = true;

  const formatTWFCartMoney = (cents, currency = 'USD') => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: value % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const isTWFLightingCartItem = (item) => {
    const text = [
      item.product_type,
      item.vendor,
      item.handle,
      item.product_title,
      item.title,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return (
      text.includes('gglighting') ||
      text.includes('light') ||
      text.includes('lighting') ||
      text.includes('u-con') ||
      text.includes('accessor') ||
      text.includes('sdr') ||
      text.includes('bumper') ||
      text.includes('tire carrier') ||
      text.includes('roof rack')
    );
  };

  const updateTWFLightingPromo = async () => {
    const promos = document.querySelectorAll('[data-twf-lighting-promo]');
    if (!promos.length) return;

    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    const lightingTotal = (cart.items || []).reduce((total, item) => {
      return isTWFLightingCartItem(item) ? total + Number(item.final_line_price || item.line_price || 0) : total;
    }, 0);
    const currency = cart.currency || window.Shopify?.currency?.active || 'USD';

    promos.forEach((promo) => {
      const threshold = Number(promo.dataset.thresholdCents || 60000);
      promo.dataset.currentCents = String(lightingTotal);
      promo.classList.toggle('is-unlocked', lightingTotal >= threshold);
    });
  };

  document.addEventListener('click', async (event) => {
    const productButton = event.target.closest('[data-twf-upsell-product-add]');
    if (productButton) {
      event.preventDefault();
      const productUrl = productButton.dataset.productUrl;
      if (productUrl) {
        window.location.href = productUrl;
        return;
      }
      const label = productButton.querySelector('[data-twf-upsell-product-label]');
      const variantId = productButton.dataset.variantId;
      if (!variantId || productButton.classList.contains('is-added')) return;

      productButton.disabled = true;
      productButton.classList.add('is-loading');
      if (label) label.textContent = 'Adding';

      try {
        await fetch(window.routes?.cart_add_url || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: Number(variantId), quantity: 1 }),
        });

        productButton.classList.add('is-added');
        if (label) label.textContent = 'Added';
        await updateTWFLightingPromo();
      } catch (error) {
        productButton.disabled = false;
        if (label) label.textContent = 'Add';
        console.error(error);
      } finally {
        productButton.classList.remove('is-loading');
      }
      return;
    }

    const button = event.target.closest('[data-twf-upsell-request]');
    if (!button) return;

    event.preventDefault();
    const label = button.querySelector('[data-twf-upsell-request-label]');
    const requestName = button.dataset.twfUpsellRequest;
    if (!requestName || button.classList.contains('is-added')) return;

    button.disabled = true;
    button.classList.add('is-loading');
    if (label) label.textContent = 'Adding';

    try {
      const cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      const attributes = cart.attributes || {};
      const attributeName = 'Requested Build Add-Ons';
      const current = (attributes[attributeName] || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
      const next = current.includes(requestName) ? current : [...current, requestName];

      await fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          attributes: {
            ...attributes,
            [attributeName]: next.join(', '),
          },
        }),
      });

      button.classList.add('is-added');
      if (label) label.textContent = 'Added';
    } catch (error) {
      button.disabled = false;
      if (label) label.textContent = 'Add';
      console.error(error);
    } finally {
      button.classList.remove('is-loading');
    }
  });
}
